import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Rating, RadioGroup, FormControlLabel, Radio, Stack} from '@mui/material';
import { toast } from 'react-toastify';
import { ICsatPortalNegociacao } from 'interfaces/Csat';
import { registrarCsatService } from 'services/Csat';

interface ICsatModalProps {
  open: boolean;
  onClose: () => void;
  cpfCnpj: string;
  contrato: string;
  acordoId: string;
  crmId?: string;
  carteiraId?: number;
}

const labels: Record<number, string> = {
  1: "Muito insatisfeito",
  2: "Insatisfeito",
  3: "Neutro",
  4: "Satisfeito",
  5: "Muito satisfeito"
};

const ModalCsat: React.FC<ICsatModalProps> = ({
  open,
  onClose,
  cpfCnpj,
  contrato,
  acordoId,
  crmId,
  carteiraId
}) => {

  const [utilidade, setUtilidade] = useState<number | null>(null);
  const [expectativa, setExpectativa] = useState<"sim" | "nao" | null>(null);
  const [loading, setLoading] = useState(false);
  const [jaRegistrado, setJaRegistrado] = useState(false);

  const resetState = () => {
    setUtilidade(null);
    setExpectativa(null);
  };

  const enviarCsat = async (mostrarToast = false) => {

    if (jaRegistrado) return;

    const dadosValidos =
      cpfCnpj &&
      contrato &&
      acordoId &&
      crmId &&
      carteiraId != null;

    if (!dadosValidos) return;

    setLoading(true);

    try {

      const payload: ICsatPortalNegociacao = {
        cpfCnpj,
        contrato,
        acordoId,
        crmId,
        carteiraId,
        utilidadeSolucao: utilidade,
        atendeuExpectativa:
          expectativa === null
            ? null
            : expectativa === "sim"
      };

      await registrarCsatService(payload);

      sessionStorage.removeItem('@acordoIdCsat');

      setJaRegistrado(true);

      if (mostrarToast) {
        toast.success("Obrigado pelo seu feedback!");
      }

    } catch {
      toast.error("Não foi possível registrar seu feedback.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    await enviarCsat(true);
    resetState();
    onClose();
  };

  const handleSkip = async () => {
    await enviarCsat(false);
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleSkip} maxWidth="sm" fullWidth>

      <DialogTitle>
        Pesquisa de satisfação
      </DialogTitle>

      <DialogContent>

        <Stack spacing={4} mt={1}>

          <Typography variant="body2" color="text.secondary">
            Sua opinião é muito importante para melhorarmos nossos serviços.
            A pesquisa leva apenas alguns segundos.
          </Typography>

          <Box sx={{ background: '#fafafa', borderRadius: 2, p: 3 }}>

            <Typography
              variant="subtitle1"
              fontWeight={500}
              textAlign="center"
              mb={2}
            >
              A solução apresentada foi útil para ajudar você a regularizar sua situação?
            </Typography>

            <Box display="flex" justifyContent="center">
              <Rating
                size="large"
                value={utilidade}
                onChange={(_, value) => setUtilidade(value)}
              />
            </Box>

            {utilidade && labels[utilidade] && (
              <Typography
                variant="body2"
                textAlign="center"
                color="text.secondary"
                mt={1}
              >
                {labels[utilidade]}
              </Typography>
            )}

          </Box>

          <Box sx={{ background: '#fafafa', borderRadius: 2, p: 3 }}>

            <Typography
              variant="subtitle1"
              fontWeight={500}
              textAlign="center"
              mb={2}
            >
              O site atendeu suas expectativas para negociação do seu contrato?
            </Typography>

            <RadioGroup
              row
              value={expectativa}
              onChange={(e) => setExpectativa(e.target.value as "sim" | "nao")}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 6
              }}
            >

              <FormControlLabel
                value="sim"
                control={<Radio />}
                label="Sim"
              />

              <FormControlLabel
                value="nao"
                control={<Radio />}
                label="Não"
              />

            </RadioGroup>

          </Box>

        </Stack>

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>

        <Button onClick={handleSkip}>
          Pular
        </Button>

        <Button
          variant="contained"
          disabled={loading || jaRegistrado}
          onClick={handleSubmit}
        >
          {loading ? "Enviando..." : "Enviar resposta"}
        </Button>

      </DialogActions>

    </Dialog>
  );
};

export default ModalCsat;